import { getBalancingServicesVolumeData } from "api/dataVisualisation/balancingServicesVolume/balancingServicesVolume";
import { getAllBidOfferData } from "api/dataVisualisation/bidOffer/bidOffer";
import { getMarketWideBidOfferAcceptancesData } from "api/dataVisualisation/bidOfferAcceptances/bidOfferAcceptances";
import {
  getMarketWideDynamicData,
  getMarketWideDynamicParametersData,
  getMarketWideDynamicRatesData,
} from "api/dataVisualisation/dynamic/dynamic";
import { getMarketWidePhysicalData } from "api/dataVisualisation/physical/physical";
import { CheckboxDataGroup } from "components/components/CheckboxList/CheckboxList";
import {
  CheckboxGroupParent,
  flattenCheckboxDataGroups,
} from "components/dataVisualization/balancingMechanismTabs/DataExporter/shared/checkboxGroupHelpers";
import { DownloadCallback } from "components/dataVisualization/dataPageComponents/MultiDatasetExporter/MultiDatasetExporter";
import { isDynamicDataset } from "models/bmuData/dynamicDataModel";
import { isDynamicParametersDataset } from "models/bmuData/dynamicParametersDataModel";
import { isRatesDataset } from "models/bmuData/dynamicRatesDataModel";
import { isPhysicalDataset } from "models/bmuData/physicalDataModel";
import { DataDownloadFormat } from "models/dataDownload/dataDownloadFormat";
import DateFilterModel from "models/filterModels/dateFilterModel";

export const transformMarketWideDownloadCallbacks = (
  datasets: string[],
  date: Date,
  bmUnits?: string[]
): DownloadCallback[] => {
  const physicalDatasets = datasets.filter(isPhysicalDataset);
  const physicalCallbacks = physicalDatasets.map((dataset) => ({
    download: (format: DataDownloadFormat) =>
      getMarketWidePhysicalData(dataset, date, bmUnits, format),
    filePrefix: `PhysicalData-${dataset}`,
  }));

  const dynamicDatasets = datasets.filter(isDynamicDataset);
  const dynamicCallbacks = dynamicDatasets.map((dataset) => ({
    download: (format: DataDownloadFormat) =>
      getMarketWideDynamicData(dataset, date, bmUnits, format),
    filePrefix: `DynamicData-${dataset}`,
  }));

  const ratesDatasets = datasets.filter(isRatesDataset);
  const ratesCallbacks = ratesDatasets.map((dataset) => ({
    download: (format: DataDownloadFormat) =>
      getMarketWideDynamicRatesData(dataset, date, bmUnits, format),
    filePrefix: `DynamicRatesData-${dataset}`,
  }));

  const dynamicParametersDatasets = datasets.filter(isDynamicParametersDataset);
  const dynamicParametersCallbacks = dynamicParametersDatasets.map(
    (dataset) => ({
      download: (format: DataDownloadFormat) =>
        getMarketWideDynamicParametersData(dataset, date, bmUnits, format),
      filePrefix: `DynamicParametersData-${dataset}`,
    })
  );

  const boalCallbacks = datasets.includes("BOAL")
    ? [
        {
          download: (format: DataDownloadFormat) =>
            getMarketWideBidOfferAcceptancesData(date, bmUnits, format),
          filePrefix: "BidOfferAcceptancesData",
        },
      ]
    : [];

  const bodCallbacks = datasets.includes("BOD")
    ? [
        {
          download: (format: DataDownloadFormat) =>
            getAllBidOfferData(date, bmUnits, format),
          filePrefix: "BidOfferData",
        },
      ]
    : [];

  const dateFilter = new DateFilterModel(date, date);
  const qasCallbacks = datasets.includes("QAS")
    ? [
        {
          download: (format: DataDownloadFormat) =>
            getBalancingServicesVolumeData(dateFilter, bmUnits, format),
          filePrefix: "BalancingServicesVolumeData",
        },
      ]
    : [];

  return [
    ...physicalCallbacks,
    ...dynamicCallbacks,
    ...ratesCallbacks,
    ...dynamicParametersCallbacks,
    ...boalCallbacks,
    ...bodCallbacks,
    ...qasCallbacks,
  ];
};

const marketViewCheckboxDataGroups: CheckboxGroupParent[] = [
  {
    name: "Physical data",
    children: [
      { name: "Bid offer acceptance level (BOAL)", datasets: ["BOAL"] },
      { name: "Final physical notification (FPN)", datasets: ["PN"] },
      { name: "Maximum export limit (MEL)", datasets: ["MELS"] },
      { name: "Maximum import limit (MIL)", datasets: ["MILS"] },
      { name: "Quiescent physical notification (QPN)", datasets: ["QPN"] },
    ],
  },
  {
    name: "Dynamic data",
    children: [
      { name: "Maximum delivery (Superseded)", datasets: ["MDV", "MDP"] },
      { name: "Maximum delivery offer/bid", datasets: ["MDB", "MDO"] },
      { name: "Minimum periods", datasets: ["MZT", "MNZT"] },
      { name: "Notices", datasets: ["NDZ", "NTB", "NTO"] },
      { name: "Rates", datasets: ["RDRE", "RURE", "RDRI", "RURI"] },
      { name: "Stable limits", datasets: ["SIL", "SEL"] },
    ],
  },
  {
    name: "Bid offer data (BOD)",
    datasets: ["BOD"],
  },
  {
    name: "Balancing services volume data (QAS)",
    datasets: ["QAS"],
  },
];

export const getMarketViewCheckboxDataGroups = (): CheckboxDataGroup[] =>
  flattenCheckboxDataGroups(marketViewCheckboxDataGroups);
